/**
 * 🔦 Drawing the torch — a post, a charred head, and the hearth's own flame.
 *
 * **T128 · D90 · `Design/samples/45`**
 *
 * ## 🔴 Why this is not in the world mesh
 *
 * `mesher.ts` emits **axis-aligned faces of a box scaled in Y**, and that is its
 * whole vocabulary. A torch is a post **0.125 m square** — thin in X *and* Z —
 * which cannot be said in that language at all. **A torch left in the world mesh
 * is a solid metre of orange.** So the mesher skips it and this draws it,
 * exactly as `flame.ts` is skipped and drawn for the hearth's fire.
 *
 * ## ✅ The flame is not a second flame
 *
 * 🎯 **haugur:** *"lit torch fire should have a little movements as camfire
 * does"*. ✅ **It is the same fire** — `buildFlames` is `flame.ts`'s own crossed
 * planes and its own 32-frame animated texture, asked for a smaller one further
 * up. **Nothing here draws a flame of its own**, and that is deliberate: two
 * flames would be two things to keep in step, and the whole point is that the
 * torch on your wall and the fire in your hearth are one fire.
 *
 * ## The proportions are Minecraft's, converted
 *
 * Its post is **2 px of 16 square and 10 px tall**. At our 1 block = 1 m that is
 * **0.125 m square and 0.625 m tall**, which is what `Design/samples/45` was
 * drawn to and what these constants are.
 */

import { BufferAttribute, BufferGeometry, Color, Mesh } from 'three';
import { MeshLambertNodeMaterial } from 'three/webgpu';
import { uniform } from 'three/tsl';
import {
  CHUNK_SIZE,
  PEG_BLOCK,
  blockIdForKey,
  isTorch,
  torchFacing,
  type TorchFacing,
} from '@lofi/shared';
import { buildFlames, type FlameSpec } from './flame.ts';

/** How thick the post is, in metres. Minecraft's 2 px of 16. */
export const POST_THICKNESS = 0.125;

/** How long the post is, in metres. Minecraft's 10 px of 16. */
export const POST_LENGTH = 0.625;

/**
 * How far a wall torch leans out of vertical, in radians.
 *
 * ⚠️ **The tilt is not decoration.** A torch drawn flat against a wall reads as
 * a stripe painted on it rather than as a thing sticking out — which is why
 * Minecraft's wall torch tilts too. 22° is the angle `Design/samples/45` was
 * drawn at and haugur approved.
 */
const WALL_TILT = (22 * Math.PI) / 180;

/** How far the flame stands above the top of the post, as a share of its height. */
const FLAME_HEIGHT = 0.42;
const FLAME_WIDTH = 0.36;

/** Peeled birch, the same values the `prik` icon and the mockup use. */
export const POST_COLOUR = 0xa5937a;

/* ── small vector helpers ─────────────────────────────────────────────────── */

type Vec = readonly [number, number, number];

const cross = (a: Vec, b: Vec): Vec => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

const unit = (a: Vec): Vec => {
  const l = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
};

const scale = (a: Vec, k: number): Vec => [a[0] * k, a[1] * k, a[2] * k];

const add = (a: Vec, b: Vec): Vec => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];

/** Four sides and a cap. */
const FACES = 5;

/* eslint-disable security/detect-object-injection --
   Numeric work over typed arrays: every index is a loop counter bounded above,
   writing into an array this caller just allocated. Same reasoning and same
   wording as `flame.ts` and `textures.ts`. */
function writeQuad(
  pos: Float32Array,
  nrm: Float32Array,
  indices: Uint32Array,
  v0: number,
  q: number,
  corners: readonly Vec[],
  normal: Vec,
): void {
  for (const [c, p] of corners.entries()) {
    const v = v0 + c;
    pos[v * 3] = p[0];
    pos[v * 3 + 1] = p[1];
    pos[v * 3 + 2] = p[2];
    nrm[v * 3] = normal[0];
    nrm[v * 3 + 1] = normal[1];
    nrm[v * 3 + 2] = normal[2];
  }
  indices[q] = v0;
  indices[q + 1] = v0 + 1;
  indices[q + 2] = v0 + 2;
  indices[q + 3] = v0;
  indices[q + 4] = v0 + 2;
  indices[q + 5] = v0 + 3;
}
/* eslint-enable security/detect-object-injection */

/** One torch in a chunk, in chunk-local cells. */
export interface TorchAt {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly facing: TorchFacing;
  /**
   * 🔦 **How far behind its block face the wall it hangs on is actually
   * drawn**, in metres — **0 for an ordinary block**, where the drawn face and
   * the block face are the same plane. **Issue #532.**
   *
   * 🔴 **A storehouse is the case where they are not.** It is a photographed
   * model drawn over a 5 × 9 × 5 box of solid cells, and its gable is drawn
   * **0.43 m inside** the box's own front face — so a torch nailed to the grid
   * hung in mid-air in front of the wall and, beside the doorway, cut through
   * the door. ⚠️ **The torch cannot work this out for itself**: it would have
   * to know which house it is on and which way that house faces. **The caller
   * asks `torchWallInset`**, which does.
   */
  readonly wallInset?: number;
  /**
   * 🪵 **A tether peg, not a torch** — D128. The same post, standing upright,
   * shorter, and with **no flame**. ⚠️ One post-drawer for both rather than a
   * second one beside it: *a second way in forgets what the first one does.*
   */
  readonly peg?: boolean;
}

/** 🪵 How tall a tether peg stands, metres — knee height. */
export const PEG_LENGTH = 0.5;
const PEG_ID = blockIdForKey(PEG_BLOCK);

/** 🪵 Every tether peg in a chunk, as upright posts. D128. */
export function pegsIn(blocks: Uint16Array): TorchAt[] {
  const out: TorchAt[] = [];
  if (PEG_ID === undefined) return out;
  for (let i = 0; i < blocks.length; i++) {
    /* eslint-disable-next-line security/detect-object-injection --
       A loop counter reading a Uint16Array. Same reasoning as torchesIn. */
    if ((blocks[i] ?? 0) !== PEG_ID) continue;
    out.push({
      x: i % CHUNK_SIZE,
      y: Math.floor(i / (CHUNK_SIZE * CHUNK_SIZE)),
      z: Math.floor(i / CHUNK_SIZE) % CHUNK_SIZE,
      facing: 'up',
      peg: true,
    });
  }
  return out;
}

/** Which way each facing leans, as a unit vector. */
const LEAN: ReadonlyMap<TorchFacing, readonly [number, number, number]> = new Map([
  ['up', [0, 1, 0]],
  ['north', [0, 0, -1]],
  ['east', [1, 0, 0]],
  ['south', [0, 0, 1]],
  ['west', [-1, 0, 0]],
]);

/**
 * Every torch in one chunk, read straight off the block array.
 *
 * ⚠️ **Scanned here rather than inside the mesher**, the same call `flame.ts`'s
 * caller already makes: the mesher is the hot loop and has its own tests, and
 * this is one pass over an array the caller is holding anyway.
 */
export function torchesIn(blocks: Uint16Array): TorchAt[] {
  const out: TorchAt[] = [];
  for (let i = 0; i < blocks.length; i++) {
    /* eslint-disable-next-line security/detect-object-injection --
       A loop counter reading a Uint16Array. Same reasoning as textures.ts. */
    const id = blocks[i] ?? 0;
    if (!isTorch(id)) continue;
    const facing = torchFacing(id);
    if (facing === undefined) continue;
    out.push({
      x: i % CHUNK_SIZE,
      y: Math.floor(i / (CHUNK_SIZE * CHUNK_SIZE)),
      z: Math.floor(i / CHUNK_SIZE) % CHUNK_SIZE,
      facing,
    });
  }
  return out;
}

/** Where one torch's post begins and ends, in chunk-local metres. */
interface Segment {
  readonly base: readonly [number, number, number];
  readonly top: readonly [number, number, number];
}

/**
 * 🔴 **Where the post actually sits, and the two cases are different.**
 *
 * A **standing** torch is upright in the middle of its cell. A **wall** torch
 * has its foot against the wall and low, and leans out and up — so its base is
 * pushed *towards* the block it hangs off and its top *away* from it.
 *
 * ⚠️ **`facing` names the way it LEANS, not the wall** (`torch.ts`), so the wall
 * is in the opposite direction and the foot goes that way. Getting this
 * backwards puts every wall torch inside the wall.
 *
 * 🔦 **And the wall is not always where the block face is** — {@link
 * TorchAt.wallInset}, issue #532. **A storehouse's gable is drawn 0.43 m behind
 * its own block face**, so the whole post moves back by that much and the 0.1 m
 * gap between the foot and the wall stays 0.1 m. ⚠️ **The inset moves the foot
 * AND the head together**: leaving the head where it was would change the
 * torch's angle, and 22° is haugur's approved drawing, not a spare parameter.
 */
export function torchSegment(t: TorchAt): Segment {
  const cx = t.x + 0.5;
  const cz = t.z + 0.5;

  if (t.facing === 'up') {
    return {
      base: [cx, t.y, cz],
      top: [cx, t.y + (t.peg === true ? PEG_LENGTH : POST_LENGTH), cz],
    };
  }

  const [lx, , lz] = LEAN.get(t.facing) ?? [0, 1, 0];
  /* The foot is 0.4 m towards the wall and 0.2 m up; the head leans out by the
     tilt. Both are read off `Design/samples/45` rather than invented here. */
  const reach = POST_LENGTH * Math.sin(WALL_TILT);
  const rise = POST_LENGTH * Math.cos(WALL_TILT);
  const back = 0.4 + (t.wallInset ?? 0);
  const footX = cx - lx * back;
  const footZ = cz - lz * back;
  return {
    base: [footX, t.y + 0.2, footZ],
    top: [footX + lx * reach, t.y + 0.2 + rise, footZ + lz * reach],
  };
}

/** The flame that stands on one torch's head. */
export function torchFlame(t: TorchAt): FlameSpec {
  const { top } = torchSegment(t);
  return {
    cx: top[0],
    /* Sunk slightly into the head, so the fire comes OUT of the torch rather
       than balancing on it — the same trick and the same reason as the
       hearth's `FLAME_SINK`. */
    cy: top[1] - 0.06,
    cz: top[2],
    height: FLAME_HEIGHT,
    width: FLAME_WIDTH,
  };
}

/* ⚠️ No suppression here: every typed-array write lives in `writeQuad`, which
   carries its own. */

/**
 * The posts of every torch in a chunk, as one geometry.
 *
 * **Four sides and a cap.** No bottom: a torch's foot is either in the ground or
 * against a wall, and a face nobody can see is a face not worth emitting — the
 * same rule the mesher's culling follows.
 *
 * ⚠️ **Normals are real**, so the sun shades the four sides differently and the
 * post reads as round-ish rather than as a flat stick. That is the whole reason
 * this uses a Lambert material and not the flame's unshaded one: **a flame must
 * never go dim on its north side and a piece of wood must.**
 */
export function buildTorchGeometry(torches: readonly TorchAt[]): BufferGeometry | null {
  return buildBeamGeometry(torches.map(torchBeam));
}

/**
 * 🪵 **A square length of wood from one point to another** — a torch's post, a
 * peg, or one rail of a gate (D130).
 *
 * 🔴 **ONE DRAWER FOR EVERY STICK IN THE WORLD.** The gate could have had its
 * own box code; *a second way in forgets what the first one does*, and this
 * one's winding was already wrong once and is now tested face by face.
 */
export interface Beam {
  readonly base: readonly [number, number, number];
  readonly top: readonly [number, number, number];
  /** Across the beam, metres. A torch post is {@link POST_THICKNESS}. */
  readonly thickness: number;
}

/** 🔦 A torch's post as a {@link Beam}. */
export function torchBeam(t: TorchAt): Beam {
  const { base, top } = torchSegment(t);
  return { base, top, thickness: POST_THICKNESS };
}

/** Every {@link Beam} as one geometry, or `null` for none. */
export function buildBeamGeometry(beams: readonly Beam[]): BufferGeometry | null {
  if (beams.length === 0) return null;

  const verts = beams.length * FACES * 4;
  const pos = new Float32Array(verts * 3);
  const nrm = new Float32Array(verts * 3);
  const indices = new Uint32Array(beams.length * FACES * 6);

  for (const [i, beam] of beams.entries()) {
    const { base, top } = beam;

    /* The post's own axis. */
    const ax = top[0] - base[0];
    const ay = top[1] - base[1];
    const az = top[2] - base[2];
    const len = Math.hypot(ax, ay, az) || 1;
    const n: readonly [number, number, number] = [ax / len, ay / len, az / len];

    /**
     * Two directions across the post, perpendicular to it and to each other.
     *
     * 🔴 **The helper vector has to avoid being parallel to the axis**, or the
     * cross product is zero and every corner collapses onto the centre line —
     * a torch that renders as nothing at all. A standing torch's axis IS the
     * world's up, which is exactly the case a naive `cross(n, up)` gets wrong,
     * so the helper switches when the post is near-vertical.
     */
    const helper: readonly [number, number, number] = Math.abs(n[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
    const u = unit(cross(n, helper));
    const v = unit(cross(n, u));

    const h = beam.thickness / 2;

    /* The four sides, each named by the direction it faces. */
    /**
     * 🔴 **The four sides, and `across` is DERIVED rather than listed.**
     *
     * 🎯 **haugur, 2026-08-31, on the second torch:** *"partly fixed"* — the
     * hole in the ground was gone and **the post was still two thin dark
     * lines.** He was seeing straight through it.
     *
     * ⚠️ **Two of the four faces were wound backwards and back-face culled.**
     * The pairs used to be written out by hand — `[u, v]`, `[-u, v]`, `[v, u]`,
     * `[-v, u]` — and **two of those flip the handedness**, because reversing
     * `out` without reversing `across` reverses the winding with it.
     *
     * ✅ **The winding a quad needs is not a choice, it is arithmetic.** The
     * first triangle spans `across` then the post's own axis, so its normal by
     * the right-hand rule is `across × n` — and for that to come out as `out`,
     * `across` must be **`n × out`**. Derived, it cannot disagree with the
     * normal beside it; listed, it did on half the faces.
     *
     * ⚠️ **Nothing in the suite could see this** until a test was written that
     * recomputes each face's normal FROM ITS OWN CORNERS and compares.
     */
    const sides: readonly (readonly [number, number, number])[] = [
      u,
      scale(u, -1),
      v,
      scale(v, -1),
    ];

    for (const [f, out] of sides.entries()) {
      const across = cross(n, out);
      const corners: readonly (readonly [number, number, number])[] = [
        add(add(base, scale(out, h)), scale(across, -h)),
        add(add(base, scale(out, h)), scale(across, h)),
        add(add(top, scale(out, h)), scale(across, h)),
        add(add(top, scale(out, h)), scale(across, -h)),
      ];
      writeQuad(pos, nrm, indices, (i * FACES + f) * 4, (i * FACES + f) * 6, corners, out);
    }

    /* The cap on the end, facing along the post. No bottom: a torch's foot is
       in the ground or against a wall, and a face nobody can see is a face not
       worth emitting — the mesher's own culling rule. */
    const cap: readonly (readonly [number, number, number])[] = [
      add(add(top, scale(u, -h)), scale(v, -h)),
      add(add(top, scale(u, h)), scale(v, -h)),
      add(add(top, scale(u, h)), scale(v, h)),
      add(add(top, scale(u, -h)), scale(v, h)),
    ];
    writeQuad(pos, nrm, indices, (i * FACES + 4) * 4, (i * FACES + 4) * 6, cap, n);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(pos, 3));
  geometry.setAttribute('normal', new BufferAttribute(nrm, 3));
  geometry.setIndex(new BufferAttribute(indices, 1));
  return geometry;
}

/**
 * The one material every torch post shares.
 *
 * 🔴 **`colorNode`, not `material.color` — and the difference is why the
 * first torch was WHITE.**
 *
 * 🎯 haugur's first screenshot showed a brilliant white cap over near-black
 * sides. That is what a `MeshLambertNodeMaterial` looks like when nobody has
 * given it a colour node: **the plain `.color` property is not what a
 * NodeMaterial reads**, so the post kept the default white, and this world is
 * lit hard from above — so the cap blew out and the four sides went to almost
 * nothing.
 *
 * ✅ **The world's own material has always done it this way**
 * (`material.colorNode = …` in `main.ts`); the torch simply did not copy it.
 * ⚠️ **A `Color` rather than a raw triple**, so three converts sRGB to linear
 * itself and the wood matches the `prik` icon it was taken from.
 */
export function buildTorchMaterial(): MeshLambertNodeMaterial {
  const material = new MeshLambertNodeMaterial();
  material.colorNode = uniform(new Color(POST_COLOUR));
  return material;
}

/** A chunk's torch posts as one object, or `null` if there are none. */
export function buildTorchMesh(
  torches: readonly TorchAt[],
  material: MeshLambertNodeMaterial,
  /** 🚪 More wood in the same mesh and the same material — the gates, D130. */
  beams: readonly Beam[] = [],
): Mesh | null {
  const geometry = buildBeamGeometry([...torches.map(torchBeam), ...beams]);
  if (geometry === null) return null;
  return new Mesh(geometry, material);
}

/** Every torch's flame in a chunk, ready for {@link buildFlames}. */
export function torchFlames(torches: readonly TorchAt[]): FlameSpec[] {
  /* 🪵 A peg never burns. */
  return torches.filter((t) => t.peg !== true).map(torchFlame);
}

export { buildFlames };
